<!-- BR - 数据Diagnosis Chart组件 -->
<template>
  <div>
    <!-- <div v-if="tagData[chartkey]" class="chart-mode-choose">
      <div
        v-for="(item, index) in tagData[chartkey]"
        :key="index"
        :class="{ 'mode-tag': true, 'mode-selected': index === currentMode }"
        @click="chooseMode(index)"
      >
        <p>{{ item.name }}</p>
      </div>
    </div> -->
    <div class="chart-canvas-container">
      <div
        ref="wrap"
        :class="{ 'chart-canvas': true, 'chart-canvas-hor': direction === 'horizontal' }"
        :style="{ height: height + 'px' }"
      ></div>
      <div
        v-if="direction === 'vertical' && showFullscreenButton"
        class="chart-canvas-btn needsclick"
        :class="{ 'chart-canvas-btn--with-legend': showCustomLegend && currentLegendItems.length }"
        :style="
          chartOptions.grid
            ? { bottom: chartOptions.grid.bottom + 'px', right: chartOptions.grid.right + 'px' }
            : {}
        "
        @click.stop="showHor(chartkey)"
      >
        <i class="crm-quanping1"></i>
      </div>
    </div>
    <DiagnosisLegendBR
      v-if="showCustomLegend && currentLegendItems.length"
      :items="currentLegendItems"
      :active="legendActive"
      :direction="direction"
      :show-collapse="false"
      @toggle="handleLegendToggle"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts'
import EventBus from '@/pages-v2/ShopDetail/components/DiagnosisCommon/utils/eventBus'
import DiagnosisLegendBR from '../Legend/index.vue'
import caculateWidthBy10FontSize from '@/pages-v2/ShopDetail/components/DiagnosisCommon/utils/caculateFontSize.js'

// 用于给当前点击的堆叠柱补一层高亮描边，避免直接改业务 series 影响 tooltip / legend 行为
const STACKED_BAR_BORDER_OVERLAY_SERIES_NAME = '__stacked_bar_border_overlay__'

// 针对多个柱状图堆叠的情况
function hasMultiSeriesStackedBars(seriesList = []) {
  const stackCountMap = {}

  seriesList.forEach(series => {
    if (
      series?.type === 'bar' &&
      series?.stack &&
      series?.name !== STACKED_BAR_BORDER_OVERLAY_SERIES_NAME
    ) {
      stackCountMap[series.stack] = (stackCountMap[series.stack] || 0) + 1
    }
  })

  return Object.values(stackCountMap).some(count => count >= 1)
}

/**
 * 为选中的堆叠柱生成一层蓝色高亮描边覆盖层
 *
 * 设计目的：
 * 用户点击/hover 堆叠柱时，需要在整根堆叠柱外围显示一圈蓝色边框（#B3D3FF）来标识选中态。
 * 不能直接修改业务 series 的 itemStyle，否则会影响 tooltip 显示和 legend 联动行为。
 * 因此通过额外插入一个 type: 'custom' 的辅助系列，用 renderItem 自定义绘制一个透明填充 + 蓝色描边的矩形，
 * 覆盖在堆叠柱上方，实现视觉上的选中高亮效果。
 *
 * 实现流程：
 * 1. 过滤出所有可见的堆叠柱状系列（排除辅助覆盖层自身、被隐藏的系列）
 * 2. 累加选中 dataIndex 下所有可见柱段的值，得到整根堆叠柱的总高度 totalValue
 * 3. 如果没有可见值，返回 null（不生成覆盖层）
 * 4. 返回一个 custom 类型的 series，通过 renderItem 绘制一个从 0 到 totalValue 的矩形描边
 *
 * @param {Object} params
 * @param {Array}  params.seriesList          - 当前图表的所有系列（含业务系列和可能已存在的覆盖层）
 * @param {Array}  params.hiddenSeriesNames   - 被图例隐藏的系列名称列表
 * @param {number|null} params.selectedBarDataIndex - 选中的柱子索引（null 表示未选中）
 * @returns {Object|null} ECharts custom series 配置，或 null（未选中/无可见值时）
 */
function buildStackedBarBorderOverlaySeries({
  seriesList = [],
  hiddenSeriesNames = [],
  selectedBarDataIndex = null,
}) {
  // 未选中任何柱子时不生成覆盖层
  if (selectedBarDataIndex === null || selectedBarDataIndex === undefined) return null

  // 筛选出所有可见的堆叠柱状系列（排除覆盖层自身和被隐藏的系列）
  const baseBarSeries = seriesList.filter(series => {
    return (
      series?.type === 'bar' &&
      series?.stack &&
      series?.name !== STACKED_BAR_BORDER_OVERLAY_SERIES_NAME &&
      !hiddenSeriesNames.includes(series?.name)
    )
  })

  if (!baseBarSeries.length) return null

  // 取第一个柱状系列作为基准，读取 barWidth、xAxisIndex、yAxisIndex 等布局参数
  const overlayBaseSeries = baseBarSeries[0]

  // 累加选中 dataIndex 下所有可见柱段的值，得到整根堆叠柱的总高度
  let totalValue = 0
  let hasVisibleValue = false

  baseBarSeries.forEach(series => {
    const currentData = series.data?.[selectedBarDataIndex]
    // data 可能是纯数字，也可能是 { value: xxx } 对象（带 itemStyle 的情况）
    const currentValue =
      typeof currentData === 'object' && currentData !== null ? currentData.value : currentData
    const currentNumericValue = Number(currentValue ?? 0)

    if (!Number.isNaN(currentNumericValue) && currentNumericValue !== 0) {
      totalValue += currentNumericValue
      hasVisibleValue = true
    }
  })

  // 所有柱段值都为 0 时不绘制描边
  if (!hasVisibleValue) return null

  const barWidth = Number(overlayBaseSeries.barWidth) || 8

  return {
    name: STACKED_BAR_BORDER_OVERLAY_SERIES_NAME,
    type: 'custom', // 自定义绘制，完全控制渲染输出
    coordinateSystem: 'cartesian2d',
    xAxisIndex: overlayBaseSeries.xAxisIndex || 0,
    yAxisIndex: overlayBaseSeries.yAxisIndex || 0,
    silent: true, // 不响应鼠标事件，避免干扰业务系列的交互
    animation: false, // 禁用动画，选中态需要即时响应
    z: 30, // 确保绘制在业务柱状系列之上
    zlevel: 1,
    tooltip: {
      show: false, // 不参与 tooltip 展示
    },
    /**
     * 自定义渲染函数：绘制一个覆盖整根堆叠柱的透明矩形 + 蓝色描边
     *
     * data 格式为 [[dataIndex, totalValue]]，通过 api.value(0) 获取 dataIndex，
     * api.value(1) 获取 totalValue，再用 api.coord() 转换为画布像素坐标。
     */
    renderItem(params, api) {
      const dataIndex = api.value(0)
      const value = api.value(1)
      // 将数据坐标转换为画布像素坐标
      const topPoint = api.coord([dataIndex, value]) // 堆叠柱顶部
      const bottomPoint = api.coord([dataIndex, 0]) // 堆叠柱底部（y=0）
      const coordSys = params.coordSys

      if (!topPoint || !bottomPoint || !coordSys) return null

      // 计算矩形的位置和尺寸（以柱子中心为基准，左右各展开 barWidth/2）
      const x = topPoint[0] - barWidth / 2
      const y = topPoint[1]
      const width = barWidth
      const height = Math.max(bottomPoint[1] - topPoint[1], 0)

      return {
        type: 'rect',
        shape: {
          x,
          y,
          width,
          height,
          r: 4, // 圆角半径，与业务柱状系列保持一致
        },
        style: {
          fill: 'transparent', // 透明填充，不遮挡柱子颜色
          stroke: '#B3D3FF', // 蓝色描边
          lineWidth: 4,
        },
        silent: true,
      }
    },
    // 只传入选中的那一根柱子的数据：[dataIndex, totalValue]
    data: [[selectedBarDataIndex, totalValue]],
  }
}

export default {
  components: {
    DiagnosisLegendBR,
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 0,
    },
    chartkey: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'vertical',
    },
    hiddenSeriesNames: {
      type: Array,
      default: () => [],
    },
    legendSelectedMap: {
      type: Object,
      default: () => ({}),
    },
    /** 是否展示右下角全屏按钮（竖向图表） */
    showFullscreenButton: {
      type: Boolean,
      default: true,
    },
    showCustomLegend: {
      type: Boolean,
      default: false,
    },
    customLegendItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentMode: 0,
      selectedBarDataIndex: null,
      legendActive: [],
      borderRadiusCache: new Map(),
    }
  },
  computed: {
    currentLegendItems() {
      return this.customLegendItems || []
    },
    mergedLegendSelectedMap() {
      return {
        ...this.currentLegendSelectedMap,
        ...this.legendSelectedMap,
      }
    },
    currentLegendSelectedMap() {
      return this.currentLegendItems.reduce((acc, item, index) => {
        acc[item.name] = this.legendActive[index] !== false
        return acc
      }, {})
    },
    /**
     * 构建 ECharts 图表配置
     * 主要功能：
     * 1. 根据图例选择状态过滤可见的系列
     * 2. 为堆叠柱状图动态计算圆角（顶部/底部/全部）
     * 3. 添加选中态边框覆盖层（蓝色高亮边框）
     * 4. 配置 tooltip 元数据
     */
    chartOptions() {
      // 获取当前模式的图表配置
      const currentOption = this.options[this.currentMode] || {}
      // 判断系列是否可见的辅助函数
      const isSeriesVisible = seriesName => this.mergedLegendSelectedMap[seriesName] !== false

      // ========== 步骤 1: 过滤可见系列并计算堆叠柱状图圆角 ==========
      const nextSeries = (currentOption.series || [])
        .filter(series => {
          // 仅过滤掉边框覆盖层系列（这是我们自己添加的辅助系列）
          if (series?.name === STACKED_BAR_BORDER_OVERLAY_SERIES_NAME) return false
          return true
        })
        .map(series => {
          // 被图例隐藏的系列：保留 series 但清空 data，避免 yAxis 跟着消失
          if (!isSeriesVisible(series?.name)) {
            return { ...series, data: [] }
          }

          // ========== 折线图处理 ==========
          if (series.type === 'line') {
            // 禁用折线动画：避免 setOption({ notMerge: true }) 重绘时数据点闪烁
            // 必须始终设置，而非仅在选中时设置，否则首次选中会因 animation 配置变化触发过渡动画
            const lineSeries = { ...series, animation: false }

            // 选中柱子时，在对应位置显示 emphasis 样式的数据点
            if (this.selectedBarDataIndex != null && Array.isArray(series.data)) {
              const emphasisStyle = series.emphasis?.itemStyle || {}
              const selectedIdx = this.selectedBarDataIndex
              const lineData = series.data.map((item, idx) => {
                if (idx !== selectedIdx) return item
                const raw = typeof item === 'object' && item !== null ? item : { value: item }
                return {
                  ...raw,
                  symbol: 'circle',
                  symbolSize: 12,
                  itemStyle: { ...emphasisStyle },
                }
              })
              return {
                ...lineSeries,
                showSymbol: true,
                symbol: 'none',
                z: 60,
                zlevel: 2,
                data: lineData,
              }
            }

            return lineSeries
          }

          // 非柱状图或非堆叠柱状图，直接返回原配置
          if (series.type !== 'bar' || !series.stack) return series

          // 当前系列是否被隐藏
          const isHidden = this.hiddenSeriesNames.includes(series.name)

          // 找出同一堆叠组中所有可见的柱状图系列
          // 用于计算当前系列在堆叠中的位置（顶部/中间/底部）
          const visibleStackSeries = (currentOption.series || []).filter(currentSeries => {
            return (
              currentSeries.type === 'bar' &&
              currentSeries.stack === series.stack &&
              currentSeries.name !== STACKED_BAR_BORDER_OVERLAY_SERIES_NAME &&
              !this.hiddenSeriesNames.includes(currentSeries.name) &&
              isSeriesVisible(currentSeries.name)
            )
          })

          // ========== 步骤 2: 为每个数据点计算圆角 ==========
          const nextData = (series.data || []).map((item, dataIndex) => {
            // 标准化数据格式：统一转换为对象格式 { value: xxx }
            const rawItem = typeof item === 'object' && item !== null ? item : { value: item }

            // 提取数值并验证有效性
            const value = rawItem.value
            const numericValue = Number(value ?? 0)
            const hasValue = !Number.isNaN(numericValue) && numericValue !== 0
            // 无效数据直接返回，不计算圆角
            if (!hasValue) return item

            // 使用缓存方法计算圆角
            const borderRadius = this.calculateBorderRadius(
              series,
              dataIndex,
              visibleStackSeries,
              isHidden,
            )

            // 返回带圆角配置的数据项
            return {
              ...rawItem,
              itemStyle: {
                ...(rawItem.itemStyle || {}),
                borderRadius,
                barBorderRadius: borderRadius,
              },
            }
          })

          // 返回更新后的系列配置
          return {
            ...series,
            data: nextData,
          }
        })

      // ========== 步骤 3: 添加选中态边框覆盖层 ==========
      // 检查是否有多系列堆叠柱状图（需要边框高亮效果）
      const shouldUseStackedBarOverlay = hasMultiSeriesStackedBars(nextSeries)

      // 如果需要且有选中的柱子，生成边框覆盖层系列
      const overlaySeries = shouldUseStackedBarOverlay
        ? buildStackedBarBorderOverlaySeries({
            seriesList: nextSeries,
            hiddenSeriesNames: this.hiddenSeriesNames,
            selectedBarDataIndex: this.selectedBarDataIndex,
          })
        : null

      // ========== 步骤 4: 配置 tooltip 元数据 ==========
      const tooltip = currentOption.tooltip
      if (tooltip) {
        // 注入图表实例和 X 轴数据，供 tooltip 格式化函数使用
        tooltip.__chartMeta = {
          ...(tooltip.__chartMeta || {}),
          getChartInstance: () => this.charts,
          xAxisData:
            tooltip.__chartMeta?.xAxisData ||
            currentOption.xAxis?.data ||
            currentOption.xAxis?.[0]?.data ||
            [],
          xAxisIndex: tooltip.__chartMeta?.xAxisIndex || 0,
        }
      }

      // ========== 步骤 5: 处理 yAxis，防止隐藏 series 后 y 轴消失 ==========
      const rawYAxis = currentOption.yAxis
      let yAxisArray = []
      if (Array.isArray(rawYAxis)) {
        yAxisArray = rawYAxis
      } else if (rawYAxis) {
        yAxisArray = [rawYAxis]
      }
      const rawGrid = currentOption.grid || {}
      // containLabel 为 true 时 ECharts 自动根据标签宽度留白，无需手动调整 grid
      const useContainLabel = !!rawGrid.containLabel
      let gridOverride = null

      const fixedYAxis = yAxisArray.map((axis, axisIndex) => {
        // 检查该轴是否还有可见数据的 series 关联
        // 不仅要求 data 数组非空，还要至少有一个有效非零值
        const hasVisibleData = nextSeries.some(s => {
          const sAxisIndex = s.yAxisIndex || 0
          if (sAxisIndex !== axisIndex) return false
          if (!Array.isArray(s.data) || s.data.length === 0) return false
          return s.data.some(d => {
            const v = typeof d === 'object' && d !== null ? d.value : d
            const n = Number(v ?? 0)
            return !Number.isNaN(n) && n !== 0
          })
        })
        if (hasVisibleData) return axis
        // 该轴关联的 series 全被隐藏了，给默认刻度范围，防止轴消失
        // 百分比轴（formatter 含 %）使用 0-100，普通数值轴使用 0-10
        const isPercentAxis =
          typeof axis.axisLabel?.formatter === 'string' && axis.axisLabel.formatter.includes('%')
        const fallbackMax = isPercentAxis ? 100 : 10
        const fallbackLabel = isPercentAxis ? '100%' : String(fallbackMax)
        // 同步调整 grid 的 left/right，使其匹配 fallback 标签宽度，避免遮挡
        // 但 containLabel: true 的图表由 ECharts 自动计算间距，跳过手动覆盖
        if (!useContainLabel) {
          const fallbackLabelWidth = caculateWidthBy10FontSize(fallbackLabel) + 8
          const position = axis.position || (axisIndex === 0 ? 'left' : 'right')
          if (!gridOverride) gridOverride = { ...rawGrid }
          if (position === 'left') {
            gridOverride.left = fallbackLabelWidth
          } else {
            gridOverride.right = fallbackLabelWidth
          }
        }
        return { ...axis, min: 0, max: fallbackMax }
      })

      const finalGrid = gridOverride || rawGrid

      return {
        ...currentOption,
        grid: finalGrid,
        yAxis: fixedYAxis.length === 1 ? fixedYAxis[0] : fixedYAxis,
        legend: {
          ...(currentOption.legend || {}),
          // 不再通过 legend.selected 控制系列显隐，改为手动清空 data，保留 yAxis 展示
        },
        tooltip,
        series: overlaySeries ? [...nextSeries, overlaySeries] : nextSeries, // 添加边框覆盖层
      }
    },
  },
  watch: {
    customLegendItems: {
      handler(items) {
        const nextSelectedMap = this.mergedLegendSelectedMap
        this.legendActive = (items || []).map(item => nextSelectedMap[item.name] !== false)
      },
      immediate: true,
      deep: true,
    },
    options(nVal, oVal) {
      if (nVal !== oVal) {
        this.currentMode = 0
        this.selectedBarDataIndex = null
        this.borderRadiusCache.clear()
        if (this.direction === 'vertical') {
          this.init()
        } else {
          this.Horinit()
        }
      }
    },
    hiddenSeriesNames: {
      handler() {
        this.borderRadiusCache.clear()
        if (this.direction === 'vertical') {
          this.init()
        } else {
          this.Horinit()
        }
      },
      deep: true,
    },
    legendSelectedMap: {
      handler() {
        this.selectedBarDataIndex = null
        this.borderRadiusCache.clear()
        if (this.direction === 'vertical') {
          this.init()
        } else {
          this.Horinit()
        }
      },
      deep: true,
    },
    currentMode(nVal, oVal) {
      if (nVal !== oVal) {
        this.$emit('changeChartMode', nVal)
      }
    },
  },
  mounted() {
    this.currentMode = 0
    if (this.direction === 'vertical') {
      this.init()
    } else {
      this.Horinit()
    }
    // 接受到点击了其他区域的消息，隐藏图表的tooltip
    EventBus.$on('handleToolTipClose', () => {
      this.selectedBarDataIndex = null
      this.charts.dispatchAction({
        type: 'hideTip',
      })
      if (this.direction === 'vertical') {
        this.init()
      } else {
        this.Horinit()
      }
    })
  },
  destroyed() {
    // 移除 EventBus 监听器
    EventBus.$off('handleToolTipClose')

    // 清理 ECharts 实例
    if (this.charts) {
      // 移除所有事件监听器
      this.charts.off('click')
      this.charts.off('mouseover')
      this.charts.off('mouseout')
      this.charts.off('updateAxisPointer')

      this.charts.dispose()
      this.charts = null
    }

    // 清理缓存
    if (this.borderRadiusCache) {
      this.borderRadiusCache.clear()
    }
  },
  methods: {
    /**
     * 计算堆叠柱状图中某个系列在指定 dataIndex 上的圆角值
     *
     * 与 service.js 中 buildStackedBarSeriesWithCornerRadius 的区别：
     * - buildStackedBarSeriesWithCornerRadius 在数据构建阶段（transformer）一次性预计算所有圆角
     * - 本方法在图表渲染阶段（computed chartOptions）按需计算，用于图例切换后动态重算圆角
     *
     * 核心逻辑：
     * 1. 在当前 dataIndex 下，找出所有"可见且有值"的系列索引
     * 2. 判断当前系列是否为堆叠的顶部/底部
     * 3. 顶部加上圆角，底部加下圆角，唯一一段四角全圆角，中间层无圆角
     *
     * 性能优化：
     * 使用 borderRadiusCache（Map）缓存计算结果，避免同一数据点重复计算。
     * 缓存在 options/hiddenSeriesNames/legendSelectedMap 变化时会被清空（见 watch）。
     *
     * @param {Object} series              - 当前系列配置（需包含 name、data）
     * @param {number} dataIndex           - 当前数据点索引
     * @param {Array}  visibleStackSeries  - 同一 stack 下所有可见的系列列表
     * @param {boolean} isHidden           - 当前系列是否被图例隐藏
     * @returns {number|Array} borderRadius 值
     *   - 0: 无圆角（中间层或被隐藏）
     *   - 4: 四角全圆角（唯一一段）
     *   - [4,4,0,0]: 顶部圆角
     *   - [0,0,4,4]: 底部圆角
     */
    calculateBorderRadius(series, dataIndex, visibleStackSeries, isHidden) {
      // 构建缓存 key：系列名 + 数据索引 + 可见系列组合 + 是否隐藏
      const cacheKey = `${series.name}-${dataIndex}-${visibleStackSeries
        .map(s => s.name)
        .join(',')}-${isHidden}`

      if (this.borderRadiusCache.has(cacheKey)) {
        return this.borderRadiusCache.get(cacheKey)
      }

      const radius = 4
      let borderRadius = 0

      // 找到当前系列在可见系列列表中的位置
      const currentVisibleSeriesIndex = visibleStackSeries.findIndex(
        currentSeries => currentSeries.name === series.name,
      )

      if (!isHidden && currentVisibleSeriesIndex !== -1) {
        // 在当前 dataIndex 下，筛选出所有"有值"的可见系列索引
        const visibleSeriesIndexes = visibleStackSeries
          .map((currentSeries, index) => {
            const currentData = currentSeries.data?.[dataIndex]
            // data 项可能是纯数字或 { value: xxx } 对象
            const currentValue =
              typeof currentData === 'object' && currentData !== null
                ? currentData.value
                : currentData
            const currentNumericValue = Number(currentValue ?? 0)
            const currentHasValue = !Number.isNaN(currentNumericValue) && currentNumericValue !== 0
            return currentHasValue ? index : -1
          })
          .filter(index => index !== -1)

        if (visibleSeriesIndexes.length) {
          // 最后一个有值的系列为顶部，第一个为底部
          const isTop =
            currentVisibleSeriesIndex === visibleSeriesIndexes[visibleSeriesIndexes.length - 1]
          const isBottom = currentVisibleSeriesIndex === visibleSeriesIndexes[0]

          if (isTop && isBottom) borderRadius = radius // 唯一一段：四角全圆角
          else if (isTop) borderRadius = [radius, radius, 0, 0] // 顶部：上圆下方
          else if (isBottom) borderRadius = [0, 0, radius, radius] // 底部：上方下圆
        }
      }

      this.borderRadiusCache.set(cacheKey, borderRadius)
      return borderRadius
    },
    handleLegendToggle({ index, name }) {
      this.$emit('legend-toggle', { index, name })
      const next = [...this.legendActive]
      next[index] = !next[index]
      if (next.some(Boolean)) {
        this.legendActive = next
      }
    },
    showHor(chartKey) {
      this.$emit('showHorCharts', chartKey)
    },
    // chooseMode(index) {
    //   if (this.currentMode !== index) {
    //     this.currentMode = index
    //     this.init()
    //   }
    // },
    /**
     * 横屏模式初始化图表
     * 使用 setTimeout(0) 等待 DOM 渲染完成后再初始化 ECharts 实例
     */
    Horinit() {
      setTimeout(() => {
        if (!this.charts) {
          this.charts = echarts.init(this.$refs.wrap)

          // ========== 注册交互事件：同步柱状图选中态与折线图数据点高亮 ==========
          // 设计目标：无论用户点击柱子、折线、还是柱子之间的空白区域，
          // 都能同步更新堆叠柱边框高亮和折线数据点 emphasis 样式。

          // updateAxisPointer: 响应 tooltip trigger:'axis' 模式下的轴指示器移动
          // 覆盖了柱子之间空白区域的交互（click/mouseover 无法捕获空白区域）
          this.charts.on('updateAxisPointer', params => {
            const axesInfo = params?.axesInfo?.[0]
            if (axesInfo && typeof axesInfo.value === 'number') {
              const nextIndex = axesInfo.value
              // 去重：避免同一 dataIndex 重复触发 setOption 导致不必要的重绘
              if (this.selectedBarDataIndex === nextIndex) return
              this.selectedBarDataIndex = nextIndex
              if (this.chartOptions) {
                this.charts.setOption(this.chartOptions, { notMerge: true })
              }
            }
          })

          // click: 响应系列元素的点击事件（柱子、折线数据点等）
          this.charts.on('click', params => {
            if (params?.componentType !== 'series') return
            // 忽略边框覆盖层自身的点击，避免循环触发
            if (params?.seriesName === STACKED_BAR_BORDER_OVERLAY_SERIES_NAME) return
            if (this.selectedBarDataIndex === params.dataIndex) return
            this.selectedBarDataIndex = params.dataIndex
            if (this.chartOptions) {
              this.charts.setOption(this.chartOptions, { notMerge: true })
            }
          })

          // mouseover: 响应鼠标悬停，实现 hover 时的实时高亮跟随
          this.charts.on('mouseover', params => {
            if (params?.componentType !== 'series') return
            if (params?.seriesName === STACKED_BAR_BORDER_OVERLAY_SERIES_NAME) return
            if (this.selectedBarDataIndex === params.dataIndex) return
            this.selectedBarDataIndex = params.dataIndex
            if (this.chartOptions) {
              this.charts.setOption(this.chartOptions, { notMerge: true })
            }
          })

          // mouseout: 鼠标移出系列元素时清除选中态
          this.charts.on('mouseout', params => {
            if (params?.componentType !== 'series') return
            this.selectedBarDataIndex = null
            if (this.chartOptions) {
              this.charts.setOption(this.chartOptions, { notMerge: true })
            }
          })
        }
        if (this.chartOptions) {
          this.charts.setOption(this.chartOptions, { notMerge: true })
        }
      }, 0)
    },
    /** 外层容器尺寸变化（如全屏弹层）后调用 */
    resizeChart() {
      if (this.charts) {
        this.$nextTick(() => {
          this.charts.resize()
        })
      }
    },
    setCurrentMode(mode) {
      this.currentMode = mode
      this.init()
    },
    /**
     * 竖屏模式初始化图表
     * 事件注册逻辑与 Horinit() 一致，详见 Horinit 中的注释说明
     */
    init() {
      if (!this.charts) {
        this.charts = echarts.init(this.$refs.wrap)

        // ========== 注册交互事件（与 Horinit 保持一致） ==========

        // updateAxisPointer: 覆盖柱子间空白区域的交互
        this.charts.on('updateAxisPointer', params => {
          const axesInfo = params?.axesInfo?.[0]
          if (axesInfo && typeof axesInfo.value === 'number') {
            const nextIndex = axesInfo.value
            if (this.selectedBarDataIndex === nextIndex) return
            this.selectedBarDataIndex = nextIndex
            if (this.chartOptions) {
              this.charts.setOption(this.chartOptions, { notMerge: true })
            }
          }
        })

        // click: 响应系列元素的点击事件
        this.charts.on('click', params => {
          if (params?.componentType !== 'series') return
          if (params?.seriesName === STACKED_BAR_BORDER_OVERLAY_SERIES_NAME) return
          if (this.selectedBarDataIndex === params.dataIndex) return
          this.selectedBarDataIndex = params.dataIndex
          if (this.chartOptions) {
            this.charts.setOption(this.chartOptions, { notMerge: true })
          }
        })

        // mouseover: hover 时实时高亮跟随
        this.charts.on('mouseover', params => {
          if (params?.componentType !== 'series') return
          if (params?.seriesName === STACKED_BAR_BORDER_OVERLAY_SERIES_NAME) return
          if (this.selectedBarDataIndex === params.dataIndex) return
          this.selectedBarDataIndex = params.dataIndex
          if (this.chartOptions) {
            this.charts.setOption(this.chartOptions, { notMerge: true })
          }
        })

        // mouseout: 移出时清除选中态
        this.charts.on('mouseout', params => {
          if (params?.componentType !== 'series') return
          this.selectedBarDataIndex = null
          if (this.chartOptions) {
            this.charts.setOption(this.chartOptions, { notMerge: true })
          }
        })
      }
      if (this.chartOptions) {
        this.charts.setOption(this.chartOptions, { notMerge: true })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-canvas {
  position: relative;
  margin-top: 32px;
  margin-bottom: 12px;
}
.chart-canvas-hor {
  margin-top: 8px;
}
.chart-canvas-container {
  position: relative;
  .crm-quanping1 {
    width: 18px;
    height: 18px;
    font-size: 18px;
    color: #111111;
  }
  .chart-canvas-btn {
    position: absolute;
    width: 56px;
    height: 56px;
    opacity: 1;
    background: #f2f2f5;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 10; /* 确保在 echarts 画布之上可点击 */
    touch-action: manipulation; /* 减少移动端触摸延迟，避免 passive 冲突 */
  }
  .chart-canvas-btn--with-legend {
    margin-bottom: 16px;
  }
}
.chart-mode-choose {
  display: flex;
  margin-top: 40px;
  .mode-tag {
    height: 32px;
    background: #f0f1f5;
    border-radius: 12px;
    padding: 8px 16px;
    margin-right: 24px;
    font-size: 24px;
    p {
      line-height: 32px;
      color: #595c66;
    }
  }
  .mode-selected {
    background: #ffefe7;
    p {
      color: #f76a31;
    }
  }
}
</style>
